import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import ModalContext from 'contexts/ModalContext'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { CART_PANEL_DESKTOP_LAYOUT_MIN_WIDTH } from './cartPanelConstants'

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(12)};
`

const TotalRight = styled.div`
  text-align: end;
  flex-shrink: 0;
`

const FooterPanel = styled.div`
  padding-block-start: ${rem(20)};
  padding-inline: ${rem(16)};
  padding-block-end: calc(${rem(20)} + env(safe-area-inset-bottom));
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  @container (min-width: ${rem(CART_PANEL_DESKTOP_LAYOUT_MIN_WIDTH)}) {
    padding-block: ${rem(24)};
    padding-inline: ${rem(24)};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-radius: ${props => props.theme.borderRadius.S};
  }
`

const FooterContent = styled(Group)`
  gap: ${rem(12)};

  @container (min-width: ${rem(CART_PANEL_DESKTOP_LAYOUT_MIN_WIDTH)}) {
    gap: ${rem(16)};
  }
`

const TotalSection = styled(Group)`
  gap: ${rem(4)};

  @container (min-width: ${rem(CART_PANEL_DESKTOP_LAYOUT_MIN_WIDTH)}) {
    gap: ${rem(16)};
  }
`

const Divider = styled.hr`
  width: 100%;
  height: 1px;
  border: 0;
  margin: 0;
  background: ${props => props.theme.palette.neutral.default.six};
`

const DesktopOnly = styled.div`
  display: none;

  @container (min-width: ${rem(CART_PANEL_DESKTOP_LAYOUT_MIN_WIDTH)}) {
    display: block;
  }
`

const CheckoutButton = styled(TextButton)`
  min-height: ${rem(48)};
`

const CheckoutActions = styled(Group)`
  gap: ${rem(8)};
`

const CheckoutBlockerSummary = styled(BodyText)`
  color: ${props => props.theme.palette.product.loyalty.bronze.darkForeground};
  text-align: center;
`

interface Props {
  totalPrice: number
  totalValue: number
  totalItemCount: number
  hasCheckoutReadyItems: boolean
  showReadyItemsOnlyNote: boolean
  checkoutBlockerItemCount: number
  loyaltyCalculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest>
  isLoading: boolean
  isCheckoutLoading: boolean
  isLoggedIn: boolean
  onCheckout: () => Promise<void>
}

function CartPanelFooter({
  totalPrice,
  totalValue,
  totalItemCount,
  hasCheckoutReadyItems,
  showReadyItemsOnlyNote,
  checkoutBlockerItemCount,
  loyaltyCalculationRequests,
  isLoading,
  isCheckoutLoading,
  isLoggedIn,
  onCheckout,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const handleCheckoutClick = useCallback(() => {
    if (!isLoggedIn) {
      showModal(<AccountAccessModal />)
      return
    }
    onCheckout()
  }, [isLoggedIn, showModal, onCheckout])

  const totalHeading = intl.formatMessage(
    {
      id: 'wrcligwUpGIpkDJ9iUs3',
      defaultMessage: 'Total ({count, plural, one {# item} other {# items}})',
      description: 'Cart panel footer heading showing total with item count',
    },
    { count: totalItemCount },
  )

  const checkoutLabel = !isLoggedIn
    ? intl.formatMessage({
        id: 'PsWbPJA5WZimhO6Dv0Zl',
        defaultMessage: 'Log in to checkout',
        description: 'Cart panel checkout button text when user is not logged in',
      })
    : intl.formatMessage({
        id: '7Ej3duOhiVx-nzIcPorw',
        defaultMessage: 'Book now',
        description: 'Primary checkout button text shown in the trip planner cart panel footer',
      })

  const checkoutStartIcon = useMemo(() => {
    return isCheckoutLoading ? <LoadingSpinner inline size={16} /> : undefined
  }, [isCheckoutLoading])

  const checkoutBlockerSummary = intl.formatMessage(
    {
      id: '72DOtW_TiEpooT_ZgfDH',
      defaultMessage: '{count, plural, one {# item needs details before it can be booked} other {# items need details before they can be booked}}',
      description: 'Cart footer message below checkout explaining how many trip items need more details before booking',
    },
    { count: checkoutBlockerItemCount },
  )
  const shouldShowValue = totalValue > totalPrice
  const shouldShowDivider = shouldShowValue || loyaltyCalculationRequests.length > 0

  return (
    <FooterPanel>
      <FooterContent direction="vertical">
        <TotalSection direction="vertical">
          <TotalRow>
            <div>
              <Heading variant="heading5">{totalHeading}</Heading>
              <PriceRowTaxesAndFees />
            </div>
            <TotalRight>
              {isLoading && <TextLoadingBox typography="heading5" width="8ch" />}
              {!isLoading && (
                <PriceRowPrice
                  price={totalPrice}
                  priceFormat="preciseDollar"
                  saleUnit=""
                  size="M"
                />
              )}
            </TotalRight>
          </TotalRow>
        </TotalSection>

        {shouldShowDivider && (
          <DesktopOnly>
            <Divider />
          </DesktopOnly>
        )}

        {shouldShowValue && (
          <DesktopOnly>
            <BodyText variant="medium" weight="bold" colour="success-dark">
              {intl.formatMessage(
                {
                  id: '4BsPZF9CJ9dmUiMOaTxX',
                  defaultMessage: 'This booking is valued up to {totalValue}',
                  description: 'Text showing the total value of the cart booking in the shopping cart',
                },
                { totalValue: <FormatCurrency format="roundedDollar" value={totalValue} /> },
              )}
            </BodyText>
          </DesktopOnly>
        )}

        {loyaltyCalculationRequests.length > 0 && (
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={loyaltyCalculationRequests}
            calculationType="estimate"
            size="S"
          />
        )}

        {showReadyItemsOnlyNote && checkoutBlockerItemCount === 0 && (
          <BodyText variant="small" colour="neutral-three">
            {intl.formatMessage({
              id: 'hv52vMT3ulBalYfuWSIX',
              defaultMessage: 'Checkout includes ready items only',
              description: 'Cart panel footer note explaining checkout skips items that need user attention',
            })}
          </BodyText>
        )}

        <CheckoutActions direction="vertical">
          <CheckoutButton
            kind="primary"
            size="large"
            fit="full-width"
            disabled={isLoggedIn && (isLoading || isCheckoutLoading || !hasCheckoutReadyItems)}
            aria-busy={isCheckoutLoading}
            startIcon={checkoutStartIcon}
            onClick={handleCheckoutClick}
          >
            {checkoutLabel}
          </CheckoutButton>
          {checkoutBlockerItemCount > 0 && (
            <CheckoutBlockerSummary variant="medium" weight="bold">
              {checkoutBlockerSummary}
            </CheckoutBlockerSummary>
          )}
        </CheckoutActions>
      </FooterContent>
    </FooterPanel>
  )
}

export default CartPanelFooter
